import { h, Component } from 'preact';
import util from 'preact-util';
import { route } from 'preact-router';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import Markdown from 'preact-markdown';
import MdEditor, { Plugins } from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';

import DragNDropFileupload from '../form/dragNdropFileupload';
import localUtil from '../../lib/util';
import RelatedRecordsPanel from './relatedRecordsPanel';
import { buildBodyWithRelated } from '../../lib/supportTicketEntities';
import { uploadImageFile } from '../../lib/pasteImageUpload';

MdEditor.unuse(Plugins.Logger);
MdEditor.unuse(Plugins.FullScreen);

@observer
class NewTicket extends Component {
    constructor(props) {
        super(props);
        this.state = {
            submitting: false,
            error: null,
            images: [],
            files: [],
            showImageUpload: false,
            showFileUpload: false,
            // Editor drag-and-drop / paste upload state
            draggingOver: false,
            uploadingDrop: false,
            // AI suggestion state
            showSuggestion: false,
            aiSuggestion: null,
            aiLoading: false,
            // Race selection state (for liverace)
            races: [],
            selectedRace: null,
            isLiverace: false,
            // Sensitive: hide this ticket from colleagues at the same customer
            sensitive: false,
            includeScreenshot: !!props.screenshot,
            // Help-center promo search (standalone page only)
            helpSearch: '',
        };
        this.messageInputRef = null;
        this.dragCounter = 0;
    }

    async componentDidMount() {
        const { supportTicketStore, appState, raceStore } = this.props.stores;
        supportTicketStore.resetNewTicket();
        if (this.props.reportContext) supportTicketStore.updateKeyValue('newTicketCategory', 'bug_report');
        // Text handed over from the help center («Fant du ikke svar?») — applied after the reset
        // so it is not wiped, and synced like a keystroke so the draft gets created.
        const prefill = supportTicketStore.consumeNewTicketPrefill();
        if (prefill) {
            supportTicketStore.updateKeyValue('newTicketBody', prefill);
            this.scheduleDraftSync('newTicketBody', prefill);
        }

        // Check if this is a liverace instance and load relevant races
        const { currentCustomer } = appState;
        if (currentCustomer?.frontpageItems?.liverace) {
            this.setState({ isLiverace: true });
            try {
                const response = await raceStore?.load({ query: { relevant: 1 } });
                if (response?.races) {
                    this.setState({ races: response.races });
                }
            } catch (err) {
                console.error('Failed to load races:', err);
            }
        }
    }

    componentWillUnmount() {
        const { supportTicketStore } = this.props.stores;
        if (this.draftSyncTimer) clearTimeout(this.draftSyncTimer);
        if (this.analyzeTimer) clearTimeout(this.analyzeTimer);
        // If the draft is still in a non-terminal state when the component unmounts,
        // fire a best-effort beacon so the sweeper can promote it sooner if needed.
        if (!this.props.reportContext && supportTicketStore.draftUuidv4 && !this.state.showSuggestion) {
            supportTicketStore.sendDraftOutcomeBeacon('modal_closed');
        }
    }

    handleInputChange = (field) => (e) => {
        const { supportTicketStore } = this.props.stores;
        const value = e.target.value;
        supportTicketStore.updateKeyValue(field, value);
        this.scheduleDraftSync(field, value);
    }

    scheduleDraftSync = (field, value) => {
        // The in-page report is sent explicitly; cancelling it must not promote an abandoned draft.
        if (this.props.reportContext) return;
        const { supportTicketStore } = this.props.stores;

        // Create the draft on first body keystroke if it doesn't exist yet.
        if (field === 'newTicketBody' && !supportTicketStore.draftUuidv4 && value.length > 0) {
            const { newTicketCategory, newTicketPriority } = supportTicketStore;
            const { selectedRace } = this.state;
            supportTicketStore.createDraft({
                category: newTicketCategory,
                priority: newTicketPriority,
                race: selectedRace,
            });
        }

        // Debounce the body update at 750ms (project convention).
        if (this.draftSyncTimer) clearTimeout(this.draftSyncTimer);
        this.draftSyncTimer = setTimeout(() => {
            const update = {};
            if (field === 'newTicketBody') update.body = value;
            if (field === 'newTicketCategory') update.category = value;
            if (field === 'newTicketPriority') update.priority = value;
            if (Object.keys(update).length > 0) {
                supportTicketStore.updateDraft(update);
            }
        }, 750);
    }

    // MdEditor onChange passes { text, html } (not a DOM event). Feed the body
    // into the same store field the textarea used and keep the draft auto-save.
    handleBodyChange = (content) => {
        const { supportTicketStore } = this.props.stores;
        const value = content.text;
        supportTicketStore.updateKeyValue('newTicketBody', value);
        this.scheduleDraftSync('newTicketBody', value);
        this.scheduleDraftAnalyze(value);
    }

    // Debounced (1.5s) AI analysis of the draft body. Guards: logged-in user with a
    // customer scope (pickers need it), min 25 chars, skip if unchanged since last run.
    scheduleDraftAnalyze = (value) => {
        if (this.props.reportContext) return;
        const { supportTicketStore, appState, userStore } = this.props.stores;
        const customerId = appState?.currentCustomer?.id || userStore?.customer?.id;
        if (!customerId) return;
        if (this.analyzeTimer) clearTimeout(this.analyzeTimer);
        this.analyzeTimer = setTimeout(() => {
            const body = (value || '').trim();
            if (body.length < 25) return;
            if (body === this.lastAnalyzedBody) return;
            this.lastAnalyzedBody = body;
            supportTicketStore.analyzeDraft(body, supportTicketStore.newTicketCategory);
        }, 1500);
    }

    dedentPastedText = (text) => {
        const lines = text.split('\n');
        const indents = lines
            .filter(line => line.trim().length > 0)
            .map((line) => {
                const match = line.match(/^[ \t]*/);
                return match ? match[0].length : 0;
            });
        if (indents.length === 0) return text;
        const minIndent = Math.min(...indents);
        if (minIndent === 0) return text;
        return lines.map(line => line.slice(minIndent)).join('\n');
    }

    // Intercept clipboard paste: upload pasted images (e.g. screenshots) and
    // insert them inline; dedent multi-line pasted text. Mirrors the reply editor.
    handleEditorPaste = (e) => {
        const target = e.target;
        if (!target || target.tagName !== 'TEXTAREA') return;
        const clipboard = e.clipboardData || window.clipboardData;
        if (!clipboard) return;

        const items = clipboard.items ? Array.from(clipboard.items) : [];
        const imageItems = items.filter(it => it.kind === 'file' && it.type && it.type.startsWith('image/'));
        if (imageItems.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            const filesToUpload = imageItems
                .map((it) => {
                    const file = it.getAsFile();
                    if (!file) return null;
                    // Clipboard screenshots often arrive without a filename — give them one.
                    if (file.name) return file;
                    const ext = (file.type && file.type.split('/')[1]) || 'png';
                    return new File([file], `pasted-image-${Date.now()}.${ext}`, { type: file.type || 'image/png' });
                })
                .filter(Boolean);
            if (filesToUpload.length === 0) return;
            this.setState({ uploadingDrop: true });
            Promise.all(filesToUpload.map(file => this.handleEditorImageUpload(file).catch((err) => {
                console.error('Paste image upload failed:', err);
            }))).then(() => {
                this.setState({ uploadingDrop: false });
            });
            return;
        }

        const text = clipboard.getData('text');
        if (!text || !text.includes('\n')) return;
        const dedented = this.dedentPastedText(text);
        if (dedented === text) return;
        e.preventDefault();
        e.stopPropagation();
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const before = target.value.slice(0, start);
        const after = target.value.slice(end);
        const newValue = before + dedented + after;
        const cursorPos = start + dedented.length;
        const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
        setter.call(target, newValue);
        target.dispatchEvent(new Event('input', { bubbles: true }));
        target.setSelectionRange(cursorPos, cursorPos);
    }

    // Insert markdown for an uploaded image into the editor at the cursor (matches
    // the ticket reply editor). Falls back to appending to the store body if the
    // editor ref isn't available.
    insertImageMarkdown = (img) => {
        const imageUrl = img.s3MediumLink || img.s3Link || img.s3SmallLink || img.url || img.src;
        const markdown = `![${img.name || 'image'}](${imageUrl})`;

        if (this.messageInputRef && this.messageInputRef.insertText) {
            this.messageInputRef.insertText(markdown);
        } else {
            const { supportTicketStore } = this.props.stores;
            const current = supportTicketStore.newTicketBody || '';
            const newContent = current + (current ? '\n\n' : '') + markdown;
            supportTicketStore.updateKeyValue('newTicketBody', newContent);
            this.scheduleDraftSync('newTicketBody', newContent);
        }
    }

    // Retry loading an uploaded image's thumbnail until the server has finished
    // generating the resized version (same behaviour as the reply editor). The
    // resized URL is valid immediately but 404s until the async resize job
    // completes, so we cache-bust and retry on error.
    handleImageError = (e, img, retryCount = 0) => {
        const maxRetries = 5;
        const retryDelay = 2000; // 2 seconds between retries

        if (retryCount < maxRetries) {
            setTimeout(() => {
                const src = img.s3ThumbLink || img.s3SmallLink || img.s3MediumLink || img.s3Link || img.src;
                e.target.src = `${src}?retry=${retryCount + 1}`;
                e.target.dataset.retryCount = retryCount + 1;
            }, retryDelay);
        }
    }

    // Upload a single image File to /api/fileupload, add it to the attachments
    // array, and insert its markdown inline — same path the reply editor uses.
    handleEditorImageUpload = (file) => new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('files[]', file, encodeURIComponent(file.name));
        const xhr = new XMLHttpRequest();
        xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 201) {
                    const response = JSON.parse(xhr.responseText);
                    const uploadedFile = response.data.files[0];
                    const imageUrl = uploadedFile.s3MediumLink || uploadedFile.s3Link || uploadedFile.s3SmallLink;
                    const { images } = this.state;
                    this.setState({ images: [...images, uploadedFile] });
                    this.insertImageMarkdown(uploadedFile);
                    resolve(imageUrl);
                } else {
                    reject(new Error(`Upload failed: ${xhr.status}`));
                }
            }
        });
        xhr.open('POST', `${util.getApiServer()}/api/fileupload`);
        xhr.setRequestHeader('Authorization', `Bearer ${util.getJwtToken()}`);
        xhr.send(formData);
    });

    handleDropZoneDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragCounter += 1;
        if (e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.indexOf('Files') !== -1) {
            this.setState({ draggingOver: true });
        }
    }

    handleDropZoneDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragCounter -= 1;
        if (this.dragCounter === 0) {
            this.setState({ draggingOver: false });
        }
    }

    handleDropZoneDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    handleDropZoneDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragCounter = 0;
        this.setState({ draggingOver: false });

        const files = e.dataTransfer && e.dataTransfer.files;
        if (!files || files.length === 0) {
            return;
        }

        const imageFiles = [];
        for (let i = 0; i < files.length; i += 1) {
            if (files[i].type && files[i].type.startsWith('image/')) {
                imageFiles.push(files[i]);
            }
        }

        if (imageFiles.length === 0) {
            return;
        }

        this.setState({ uploadingDrop: true });
        for (let i = 0; i < imageFiles.length; i += 1) {
            try {
                await this.handleEditorImageUpload(imageFiles[i]);
            } catch (err) {
                console.error('Drop upload failed:', err);
            }
        }
        this.setState({ uploadingDrop: false });
    }

    handleAddImage = (file) => {
        const { images } = this.state;
        this.setState({
            images: [...images, file],
            showImageUpload: false,
        });
        this.insertImageMarkdown(file);
    }

    handleAddFile = (file) => {
        const { files } = this.state;
        this.setState({
            files: [...files, file],
            showFileUpload: false,
        });
    }

    handleRemoveImage = (index) => {
        const { images } = this.state;
        this.setState({
            images: images.filter((_, i) => i !== index),
        });
    }

    handleRemoveFile = (index) => {
        const { files } = this.state;
        this.setState({
            files: files.filter((_, i) => i !== index),
        });
    }

    toggleImageUpload = () => {
        this.setState({ showImageUpload: !this.state.showImageUpload });
    }

    toggleFileUpload = () => {
        this.setState({ showFileUpload: !this.state.showFileUpload });
    }

    handleRaceChange = (e) => {
        const raceId = e.target.value ? parseInt(e.target.value, 10) : null;
        this.setState({ selectedRace: raceId });
        const { supportTicketStore } = this.props.stores;
        supportTicketStore.updateDraft({ race: raceId });
    }

    handleSubmit = async (e) => {
        e.preventDefault();

        const { supportTicketStore } = this.props.stores;
        const { newTicketBody, newTicketCategory, draftRelated } = supportTicketStore;

        if (!newTicketBody.trim()) {
            this.setState({ error: 'Message is required' });
            return;
        }

        if (this.props.reportContext) return this.handleProceedWithTicket();

        this.setState({ aiLoading: true, error: null });

        const finalBody = buildBodyWithRelated(newTicketBody, draftRelated);

        try {
            // Get AI suggestion first before creating ticket (using the enriched body)
            const suggestion = await supportTicketStore.getSuggestion(finalBody, newTicketCategory);

            if (suggestion && suggestion.suggestedAnswer) {
                // Show suggestion modal
                this.setState({
                    aiSuggestion: suggestion,
                    showSuggestion: true,
                    aiLoading: false,
                });
            } else {
                // No suggestion available, proceed directly with ticket creation
                this.setState({ aiLoading: false });
                await this.handleProceedWithTicket();
            }
        } catch (err) {
            // AI failed, proceed with ticket creation
            this.setState({ aiLoading: false });
            await this.handleProceedWithTicket();
        }
    }

    handleProceedWithTicket = async () => {
        if (this.submittingTicket) return;
        this.submittingTicket = true;
        const { supportTicketStore } = this.props.stores;
        const {
            newTicketBody, newTicketCategory, newTicketPriority, draftRelated,
        } = supportTicketStore;
        const { images, files, selectedRace, sensitive } = this.state;

        this.setState({ submitting: true, error: null, showSuggestion: false });
        this.props.onBusyChange?.(true);

        const { appState, userStore } = this.props.stores;
        const { currentCustomer } = appState;
        const { customer } = userStore;
        const customerId = currentCustomer?.id || customer?.id;

        const relatedBody = buildBodyWithRelated(newTicketBody, draftRelated);
        const finalBody = this.props.reportContext
            ? `${relatedBody}\n\n---\n${this.props.reportContext}` : relatedBody;

        try {
            let ticketImages = images;
            if (this.state.includeScreenshot && this.props.screenshot) {
                // Retain the uploaded file on a ticket failure so retrying does not upload it twice.
                if (!this.uploadedScreenshot) this.uploadedScreenshot = await uploadImageFile(this.props.screenshot);
                ticketImages = [...images, this.uploadedScreenshot];
            }
            // Subject is auto-generated by the backend
            const ticket = await supportTicketStore.createTicket(
                null, // Subject will be auto-generated
                finalBody,
                newTicketCategory,
                newTicketPriority,
                ticketImages,
                files,
                customerId,
                selectedRace,
                sensitive
            );

            if (ticket) {
                if (this.props.onSubmitted) this.props.onSubmitted(ticket);
                else route(`/support/ticket/${ticket.uuidv4}`);
            } else {
                this.setState({ error: 'Failed to create ticket. Please try again.' });
            }
        } catch (err) {
            this.setState({ error: 'An error occurred. Please try again.' });
        } finally {
            this.submittingTicket = false;
            this.props.onBusyChange?.(false);
            this.setState({ submitting: false });
        }
    }

    handleSuggestionSolved = async () => {
        const { supportTicketStore } = this.props.stores;
        await supportTicketStore.setDraftOutcome('ai_solved');
        supportTicketStore.resetNewTicket();
        this.setState({
            showSuggestion: false,
            aiSuggestion: null,
            images: [],
            files: [],
        });
        route('/support');
    }

    handleCloseSuggestion = () => {
        const { supportTicketStore } = this.props.stores;
        supportTicketStore.setDraftOutcome('modal_closed');
        this.setState({
            showSuggestion: false,
        });
    }

    handleCancel = () => {
        if (this.props.onCancel) this.props.onCancel();
        else route('/support');
    }

    // Many users land here thinking it is the help-center search. Send the query
    // to the real search (same deep-link the floating help widget uses).
    handleHelpSearch = (e) => {
        e.preventDefault();
        const search = (this.state.helpSearch || '').trim();
        if (search) {
            route(`/help/search/${encodeURIComponent(search)}`);
        } else {
            route('/help');
        }
    }

    render() {
        const { supportTicketStore } = this.props.stores;
        const { newTicketBody, newTicketCategory, newTicketPriority, categories, priorities } = supportTicketStore;
        const { submitting, error, images, files, showImageUpload, showFileUpload, draggingOver, uploadingDrop, showSuggestion, aiSuggestion, aiLoading, isLiverace, races, selectedRace, helpSearch } = this.state;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    {/* AI Suggestion Modal */}
                    {showSuggestion && aiSuggestion && (
                        <div class="modal show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
                            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                                <div class="modal-content">
                                    <div class="modal-header bg-info text-white">
                                        <h5 class="modal-title">
                                            <i class="fas fa-robot me-2" />
                                            <Text id="support.ai-suggestion-title">We found a possible answer</Text>
                                        </h5>
                                        <button type="button" class="btn-close btn-close-white" onClick={this.handleCloseSuggestion} />
                                    </div>
                                    <div class="modal-body">
                                        <div class="alert alert-info mb-3">
                                            <i class="fas fa-lightbulb me-2" />
                                            <Text id="support.ai-suggestion-intro">Based on your question, here's what we found:</Text>
                                        </div>
                                        <div class="border rounded p-3 bg-light mb-3">
                                            <Markdown markdown={aiSuggestion.suggestedAnswer} />
                                        </div>
                                        {aiSuggestion.referencedArticles && aiSuggestion.referencedArticles.length > 0 && (
                                            <div class="mb-3">
                                                <strong><Text id="support.related-articles">Related help articles:</Text></strong>
                                                <ul class="list-unstyled mt-2">
                                                    {aiSuggestion.referencedArticles.map((article) => (
                                                        <li key={article.uuidv4}>
                                                            <a href={`/#/article/${article.uuidv4}`} target="_blank" rel="noopener noreferrer">
                                                                <i class="fas fa-external-link-alt me-1" />
                                                                {article.title}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div class="modal-footer">
                                        <button
                                            type="button"
                                            class="btn btn-success rounded-pill"
                                            onClick={this.handleSuggestionSolved}
                                        >
                                            <i class="fas fa-check me-2" />
                                            <Text id="support.problem-solved">This solved my problem</Text>
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-outline-secondary rounded-pill"
                                            onClick={this.handleProceedWithTicket}
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <span>
                                                    <i class="fas fa-spinner fa-spin me-2" />
                                                    <Text id="common.submitting">Submitting...</Text>
                                                </span>
                                            ) : (
                                                <span>
                                                    <i class="fas fa-paper-plane me-2" />
                                                    <Text id="support.submit-anyway">Submit ticket anyway</Text>
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Help-center promo: many users mistake this page for the article search */}
                    {!this.props.reportContext && (
                        <div class="card mx-3 mb-3 border-info bg-info bg-opacity-10">
                            <div class="card-body">
                                <div class="d-flex align-items-start">
                                    <i class="fa-solid fa-circle-question fa-2x text-info me-3 mt-1" />
                                    <div class="flex-grow-1">
                                        <h5 class="mb-1 text-body">
                                            <Text id="support.help-promo-title">Looking for help?</Text>
                                        </h5>
                                        <p class="mb-3 text-body-secondary">
                                            <Text id="support.help-promo-text">The Help Center has articles and guides covering most questions - try a search before you send a ticket.</Text>
                                        </p>
                                        <form onSubmit={this.handleHelpSearch} class="d-flex flex-wrap align-items-center gap-2">
                                            <div class="input-group flex-grow-1" style="min-width: 240px; max-width: 520px;">
                                                <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" /></span>
                                                <Localizer>
                                                    <input
                                                        type="search"
                                                        class="form-control"
                                                        placeholder={<Text id="support.help-promo-search-placeholder">Search the Help Center...</Text>}
                                                        value={helpSearch}
                                                        onInput={e => this.setState({ helpSearch: e.target.value })}
                                                    />
                                                </Localizer>
                                                <button type="submit" class="btn btn-info">
                                                    <Text id="support.help-promo-search">Search</Text>
                                                </button>
                                            </div>
                                            <a href="/help" class="text-decoration-none">
                                                <Text id="support.help-promo-open">Open the Help Center</Text>
                                                <i class="fa-solid fa-arrow-right ms-1" />
                                            </a>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div class="card mx-3">
                            <div class="card-header">
                                <h5 class="mb-0">
                                    <i class="fas fa-plus-circle me-2" /> <Text id="support.new-ticket-title">New Support Ticket</Text>
                                </h5>
                            </div>
                            <div class="card-body">
                                {error && (
                                    <div class="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={this.handleSubmit}>
                                    {this.state.includeScreenshot && this.props.screenshotUrl && (
                                        <div class='mb-3'>
                                            <div class='d-flex align-items-center justify-content-between mb-2'>
                                                <strong><Text id='support.reportProblem.screenshot'>
                                                    Screenshot
                                                </Text></strong>
                                                <button type='button' class='btn btn-sm btn-outline-secondary'
                                                    disabled={submitting}
                                                    onClick={() => this.setState({ includeScreenshot: false })}>
                                                    <Text id='support.reportProblem.removeScreenshot'>
                                                        Remove screenshot
                                                    </Text>
                                                </button>
                                            </div>
                                            <img src={this.props.screenshotUrl} alt='Screenshot'
                                                class='img-fluid border rounded'
                                                style='max-height: 260px; object-fit: contain;' />
                                        </div>
                                    )}
                                    <div class="form-row">
                                        <div class="form-group col-md-6">
                                            <label for="category">
                                                <Text id="support.category">Category</Text>
                                            </label>
                                            <select
                                                class="form-control"
                                                id="category"
                                                value={newTicketCategory}
                                                onChange={this.handleInputChange('newTicketCategory')}
                                            >
                                                {categories.map((cat) => (
                                                    <option key={cat} value={cat}>
                                                        {cat.replace(/_/g, ' ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div class="form-group col-md-6">
                                            <label for="priority">
                                                <Text id="support.priority">Priority</Text>
                                            </label>
                                            <select
                                                class="form-control"
                                                id="priority"
                                                value={newTicketPriority}
                                                onChange={this.handleInputChange('newTicketPriority')}
                                            >
                                                {priorities.map((pri) => (
                                                    <option key={pri} value={pri}>
                                                        {pri}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div class="form-group form-check mb-3">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="sensitiveTicket"
                                            checked={this.state.sensitive}
                                            onChange={() => this.setState({ sensitive: !this.state.sensitive })}
                                        />
                                        <label class="form-check-label" for="sensitiveTicket">
                                            <i class="fas fa-lock me-1" />
                                            <Text id="support.sensitive-label">Sensitive - hide this ticket from my colleagues</Text>
                                        </label>
                                        <div class="form-text">
                                            <Text id="support.sensitive-help">Only you and the support team will be able to see this ticket.</Text>
                                        </div>
                                    </div>

                                    {/* Race selector (only visible for liverace instances) */}
                                    {isLiverace && races.length > 0 && (
                                        <div class="form-group">
                                            <label for="race">
                                                <Text id="support.race">Race</Text>
                                            </label>
                                            <select
                                                class="form-control"
                                                id="race"
                                                value={selectedRace || ''}
                                                onChange={this.handleRaceChange}
                                            >
                                                <option value="">-- Select a race --</option>
                                                {races.map((race) => (
                                                    <option key={race.id} value={race.id}>
                                                        {race.title || race.name || `Race ${race.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div class="form-group">
                                        <label for="body">
                                            <Text id="support.message">Message</Text> *
                                        </label>
                                        <div onPasteCapture={this.handleEditorPaste}>
                                            <MdEditor
                                                ref={(el) => { this.messageInputRef = el; }}
                                                style={{ height: '300px' }}
                                                renderHTML={text => Markdown(text, { markupOpts: localUtil.getMarkdownOptions() })}
                                                onChange={this.handleBodyChange}
                                                value={newTicketBody || ''}
                                                placeholder="Describe your issue in detail..."
                                                view={{ menu: true, md: true, html: false }}
                                                readOnly={submitting || aiLoading}
                                            />
                                        </div>
                                        <div class="text-body-secondary small mt-1">
                                            <i class="fa-regular fa-clipboard me-1" />
                                            <Text id="support.paste-image-hint">Tip: you can paste a screenshot straight into the message.</Text>
                                        </div>
                                    </div>

                                    {!this.props.reportContext && <RelatedRecordsPanel
                                        entities={supportTicketStore.draftEntities}
                                        draftRelated={supportTicketStore.draftRelated}
                                        onAdd={(type, record) => supportTicketStore.addRelatedRecord(type, record)}
                                        onRemove={(type, id) => supportTicketStore.removeRelatedRecord(type, id)}
                                    />}

                                    {/* Attachments Section (also a drag-and-drop upload zone) */}
                                    <div
                                        class={`form-group position-relative ${draggingOver ? 'border border-primary border-2 rounded bg-primary bg-opacity-10' : ''}`}
                                        onDragEnter={this.handleDropZoneDragEnter}
                                        onDragLeave={this.handleDropZoneDragLeave}
                                        onDragOver={this.handleDropZoneDragOver}
                                        onDrop={this.handleDropZoneDrop}
                                        style={draggingOver ? { minHeight: '80px', padding: '12px' } : {}}
                                    >
                                        <label><Text id="support.attachments">Attachments</Text></label>
                                        {draggingOver && (
                                            <div class="text-center text-primary py-3">
                                                <i class="fas fa-cloud-upload-alt fa-2x mb-2 d-block" />
                                                <strong><Text id="support.drop-images-here">Drop images here to upload</Text></strong>
                                            </div>
                                        )}
                                        {uploadingDrop && (
                                            <div class="text-center text-body-secondary py-2">
                                                <i class="fas fa-spinner fa-spin me-2" />
                                                <Text id="common.uploading">Uploading...</Text>
                                            </div>
                                        )}
                                        <div class="d-flex gap-2 mb-2">
                                            <button
                                                type="button"
                                                class="btn btn-outline-secondary btn-sm"
                                                onClick={this.toggleImageUpload}
                                            >
                                                <i class="fas fa-image me-1" />
                                                <Text id="support.add-image">Add Image</Text>
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-outline-secondary btn-sm"
                                                onClick={this.toggleFileUpload}
                                            >
                                                <i class="fas fa-paperclip me-1" />
                                                <Text id="support.add-file">Add File</Text>
                                            </button>
                                        </div>

                                        {/* Image Upload */}
                                        {showImageUpload && (
                                            <div class="mb-3 p-3 border rounded bg-light">
                                                <DragNDropFileupload
                                                    handleAddImage={this.handleAddImage}
                                                    apiUrl="/api/fileupload"
                                                    stores={this.props.stores}
                                                    accept="image/*"
                                                    imagesOnly={true}
                                                    autoOpen={false}
                                                    field="ticket-images"
                                                    text="Upload image"
                                                />
                                            </div>
                                        )}

                                        {/* File Upload */}
                                        {showFileUpload && (
                                            <div class="mb-3 p-3 border rounded bg-light">
                                                <DragNDropFileupload
                                                    handleAddImage={this.handleAddFile}
                                                    apiUrl="/api/fileupload"
                                                    stores={this.props.stores}
                                                    accept="*/*"
                                                    imagesOnly={false}
                                                    autoOpen={false}
                                                    field="ticket-files"
                                                    text="Upload file"
                                                />
                                            </div>
                                        )}

                                        {/* Display uploaded images */}
                                        {images.length > 0 && (
                                            <div class="mb-3">
                                                <small class="text-muted d-block mb-2">Images:</small>
                                                <div class="d-flex flex-wrap gap-2">
                                                    {images.map((img, idx) => (
                                                        <div key={idx} class="position-relative" style="width: 80px;">
                                                            <img
                                                                src={img.s3ThumbLink || img.s3SmallLink || img.s3Link}
                                                                alt={img.name}
                                                                class="img-thumbnail"
                                                                style="width: 80px; height: 80px; object-fit: cover;"
                                                                onError={(e) => this.handleImageError(e, img, parseInt(e.target.dataset.retryCount || '0', 10))}
                                                                data-retry-count="0"
                                                            />
                                                            <button
                                                                type="button"
                                                                class="btn btn-danger btn-sm position-absolute"
                                                                style="top: -5px; right: -5px; padding: 0 5px; font-size: 10px;"
                                                                onClick={() => this.handleRemoveImage(idx)}
                                                            >
                                                                <i class="fas fa-times" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Display uploaded files */}
                                        {files.length > 0 && (
                                            <div class="mb-3">
                                                <small class="text-muted d-block mb-2">Files:</small>
                                                <ul class="list-group">
                                                    {files.map((file, idx) => (
                                                        <li key={idx} class="list-group-item d-flex justify-content-between align-items-center py-2">
                                                            <span>
                                                                <i class="fas fa-file me-2" />
                                                                {file.name}
                                                                <small class="text-muted ms-2">({util.formatBytes(file.bytes)})</small>
                                                            </span>
                                                            <button
                                                                type="button"
                                                                class="btn btn-outline-danger btn-sm"
                                                                onClick={() => this.handleRemoveFile(idx)}
                                                            >
                                                                <i class="fas fa-times" />
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    <div class="d-flex justify-content-end">
                                        <button
                                            type="button"
                                            class="btn btn-secondary rounded-pill mx-1"
                                            onClick={this.handleCancel}
                                            disabled={submitting || aiLoading}
                                        >
                                            <Text id="common.cancel">Cancel</Text>
                                        </button>
                                        <button
                                            type="submit"
                                            class="btn btn-primary rounded-pill mx-1"
                                            disabled={submitting || aiLoading}
                                        >
                                            {aiLoading ? (
                                                <span>
                                                    <i class="fas fa-spinner fa-spin me-2" /> <Text id="support.checking-answer">Checking for answers...</Text>
                                                </span>
                                            ) : submitting ? (
                                                <span>
                                                    <i class="fas fa-spinner fa-spin me-2" /> <Text id="common.submitting">Submitting...</Text>
                                                </span>
                                            ) : (
                                                <span>
                                                    <i class="fas fa-paper-plane me-2" /> <Text id="support.submit-ticket">Submit Ticket</Text>
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
}

export default NewTicket;
